<template>
  <div class="container-fluid d-flex flex-column h-100 onboarding-container proposal-container">
    <div class="row m-4 justify-content-center onboarding-new align-items-stretch">
      <!-- Left content column -->
      <div class="col-lg-9 col-12">
        <div class="row h-100 justify-content-center align-items-center flex-grow-1">
          <!-- Logo - visible on all screen sizes -->
          <div class="container-fluid col-12">
            <a class="navbar-brand d-none d-lg-block" href="/">
              <img src="/img/logo_png/avid_logo_black.png" alt="Avid" class="navbar-brand-logo-avidonly px-2" />
            </a>
            <div class="d-lg-none d-flex align-items-center justify-content-center">
              <img src="/img/logo_png/avid_logo_red.png" alt="Avid" class="w-25">
            </div>
          </div>
          <div class="container-fluid col-12 px-5 mt-4">
            <!-- Headline -->
            <h2 class="text-center mb-2">Avid Subscription Prepared for {{ headlineName }}</h2>
            <h4 v-if="showSubHeadline" class="text-center mb-2" >For {{ local_proposal.client.name }}</h4>
            
            <!-- Expiration notice -->
            <p class="text-center mb-4"><em>This pricing listed is valid until {{ date(local_proposal.expiration_date) }}.</em></p>

            <!-- Intro paragraph -->
            <p class="mb-3">
              Avid is the first AI-powered system that connects your CRM, email, and donation tools—and turns donor data into daily priorities and ready-to-launch campaigns, so your team can raise more without burning out.
            </p>
            <p :class="local_proposal.custom_message ? 'mb-4' : 'mb-5'">With your signature today, you'll take your first step toward more effective and impactful fundraising.</p>

            <!-- Custom message -->
            <div v-if="local_proposal.custom_message" class="alert alert-info mb-5 custom-message" v-html="local_proposal.custom_message"></div>

            <!-- Line items table -->
            <!-- Line items: styled like the mock -->
            <div class="product-table mb-4 px-2">
              <div class="pt-head row gx-0 align-items-center">
                <div class="pt-th" :class="showQuantityColumn ? 'col-7' : 'col-8'">Item &amp; Description</div>
                <div class="col-1 d-none d-md-block pt-th text-center" v-if="showQuantityColumn">Qty</div>
                <div class="col-2 d-none d-md-block pt-th text-end">Unit Price</div>
                <div class="col-2 d-none d-md-block pt-th text-end">Total</div>
              </div>


              <!-- SECTION: Items due now -->
              <div class="pt-section row gx-0" v-if="dueNowItems.length > 0">
                <div class="col-12 pt-section-title">Items due now</div>
              </div>

              <div
                v-for="item in dueNowItems"
                :key="'now-'+item.id"
                class="pt-row row gx-0 align-items-start"
              >
                <div :class="showQuantityColumn ? 'col-12 col-md-7' : 'col-12 col-md-8'" class="pt-cell">
                  <div class="pt-title">{{ item.name }}</div>
                  <div class="pt-desc" v-html="item.description"></div>
                  <div class="pt-start small fw-semibold mt-2">
                    Time Period: {{ timePeriodDisplay(item) }}
                  </div>
                </div>

                <!--Quantity-->
                <div v-if="showQuantityColumn" class="col-1 d-none d-md-block pt-cell text-center">
                  {{ qty(item) }}
                </div>

                <!--Unit Price-->
                <div class="col-2 d-none d-md-block pt-cell text-end">
                  <span class="pt-amount">{{ currency(item.total_amount/item.term/item.quantity) }}</span>
                  <span v-if="cadenceSuffix(item)" class="text-muted small ms-1">{{ cadenceSuffix(item) }}</span>
                </div>

                <!--Total-->
                <div class="col-2 d-none d-md-block  pt-cell text-end">
                  <span class="pt-amount">{{ currency(item.net_amount/item.term) }}</span>
                  <div v-if="hasDiscount(item)" class="pt-green small">
                    after {{ currency((item.total_amount - item.net_amount)/item.term) }} discount
                  </div>
                </div>

                <!--Mobile-->
                <div class="col-12 d-md-none pt-cell">
                  <div v-if="showQuantityColumn" class="text-muted">Quantity: {{ qty(item) }}</div>
                  <span class="pt-amount">{{ currency(item.net_amount/item.term) }}</span>
                  <span v-if="cadenceSuffix(item)" class="text-muted small ms-1">{{ cadenceSuffix(item) }}</span>
                  <div v-if="hasDiscount(item)" class="pt-green small">
                    after {{ currency((item.total_amount - item.net_amount)/item.term) }} discount
                  </div>
                </div>
              </div>

              <!-- SECTION: Items due later -->
              <div class="pt-section row gx-0 " v-if="dueLaterItems.length">
                <div class="col-12 pt-section-title">Items due later</div>
              </div>

              <div
                v-for="item in dueLaterItems"
                :key="'later-'+item.id"
                class="pt-row row gx-0 align-items-start"
              >
                <div :class="showQuantityColumn ? 'col-12 col-md-7' : 'col-12 col-md-8'" class="pt-cell">
                  <div class="pt-title">{{ item.name }}</div>
                  <div class="pt-desc" v-html="item.description"></div>
                  <div class="pt-start small fw-semibold mt-2">
                    Time Period: {{ timePeriodDisplay(item) }}
                  </div>
                </div>

                <!--Quantity-->
                <div v-if="showQuantityColumn" class="col-1 d-none d-md-block pt-cell text-center">
                  <span v-if="item.quantity>1">{{ qty(item) }}</span>
                  <span v-else>--</span>
                </div>

                <!--Unit Price-->
                <div class="col-2 d-none d-md-block pt-cell text-end">
                  <span class="pt-amount">{{ currency(item.total_amount/item.term/item.quantity) }}</span>
                  <span v-if="cadenceSuffix(item)" class="text-muted small ms-1">{{ cadenceSuffix(item) }}</span>
                </div>

                <!--Total-->
                <div class="col-2 d-none d-md-block  pt-cell text-end">
                  <span class="pt-amount">{{ currency(item.net_amount/item.term) }}</span>
                  <div v-if="hasDiscount(item)" class="pt-green small">
                    after {{ currency((item.total_amount - item.net_amount)/item.term) }} discount
                  </div>
                  <div v-if="item.tax_amount > 0" class="text-muted small">+ {{ currency(item.tax_amount/item.term/item.quantity) }} tax</div>
                </div>

                <!--Mobile-->
                <div class="col-12 d-md-none pt-cell">
                  <div v-if="showQuantityColumn" class="text-muted">Quantity: {{ qty(item) }}</div>
                  <span class="pt-amount">{{ currency(item.net_amount/item.term) }}</span>
                  <span v-if="cadenceSuffix(item)" class="text-muted small ms-1">{{ cadenceSuffix(item) }}</span>
                  <div v-if="hasDiscount(item)" class="pt-green small">
                    after {{ currency((item.total_amount - item.net_amount)/item.term) }} discount
                  </div>
                  <div v-if="item.tax_amount > 0" class="text-muted small">+ {{ currency(item.tax_amount/item.term/item.quantity) }} tax</div>
                </div>
              </div>

              <!-- Tax breakdown
              <div v-if="oneTimeTaxTotal > 0" class="pt-tax-breakdown row gx-0 align-items-center">
                <div class="col-10 pt-tax-label pt-cell ">Subtotal</div>
                <div class="col-2 text-end pt-tax-amount pt-cell ">{{ currency(oneTimeSubtotal) }}</div>
              </div> -->
              <div v-if="oneTimeTaxTotal > 0" class="pt-tax-breakdown row gx-0 align-items-center">
                <div class="col-7 pt-tax-label pt-cell">Sales Tax</div>
                <div class="col-5 text-end pt-tax-amount pt-cell">{{ currency(oneTimeTaxTotal) }}</div>
              </div>
              <div class="pt-due-now row gx-0 align-items-center">
                <div class="col-7 pt-due-now-label">Due now</div>
                <div class="col-5 text-end pt-due-now-amount">{{ currency(dueNowTotal) }}</div>
              </div>
            </div>

            <div v-if="isEsign" class="my-5 text-center">
              <hr>
              <div class=" text-muted fw-semibold" style="margin-top: -1.5em; background: #fff; position: relative; z-index: 1; display: inline-block; padding: 0 1em;">
                Signatures are on the next page
              </div>
            </div>
            <div v-if="isEsign" style="page-break-before: always;"></div>
            <!-- Consent text -->
            <p class="mb-2">
              This SaaS Subscription Agreement (the "Agreement") is made and entered into as of {{ effectiveDate }} (the "Effective Date"), by and between AVID Group, Inc. ("Avid"), and {{ headlineName }}.
            </p>

            <p class="mb-4">
              The term of this Agreement shall commence on the Effective Date and continue through {{ agreementEndDate }} (the "Term"). The total contract value for the Term shall be {{ currency(totalAmountDue) }} USD.
            </p>

            <!-- Expired notice -->
            <div v-if="expired" class="alert alert-warning mb-4">
              This proposal has expired and is no longer available for signing.
            </div>

            <!-- Signature section split -->
            <div v-if="!expired" class="row">
              <!-- Avid Rep -->
              <div class="col-md-6 mb-5 pe-md-3 mb-md-4">
                <h5 >Avid Representative</h5>
                <template v-if="avidSigned">
                    <img :src="getSigUrl(avidSigned)" alt="signature" class="img-fluid" /><br>
                    Signed by {{ avidSigned.signer_name }}<br>
                    <small>{{ datetime(avidSigned.signed_at) }}</small><br>
                </template>
                <template v-else-if="isAvidUser || isEsign">
                  <form @submit.prevent="submitSignature">
                    <input type="hidden" v-model="form.first_name" name="first_name">
                    <input type="hidden" v-model="form.last_name" name="last_name">
                    <input type="hidden" v-model="form.email" name="email">
                    <input type="hidden" value="1" name="agree_terms"></input>
                    <!-- Signer info display -->
                    <div class="mb-3 p-3 bg-secondary" v-if="!isEsign" style="border-radius: 20px;">
                      <div class="small text-muted mb-1">Signing as:</div>
                      <div class="fw-semibold">{{ form.first_name }} {{ form.last_name }}</div>
                      <div class="small text-muted">{{ form.email }}</div>
                    </div>
                    <div v-else>
                      <div class="row mb-3" :class="(isEsign ? 'border-0 border-bottom pb-4' : '')">
                        <div class="col">
                          <label class="form-label">First Name</label>
                          <input class="form-control" required v-show="!isEsign" aria-label="First Name">
                        </div>
                        <div class="col">
                          <label class="form-label">Last Name</label>
                          <input class="form-control" required v-show="!isEsign" aria-label="Last Name">
                        </div>
                      </div>
                      <div class="mb-3" :class="(isEsign ? 'border-0 border-bottom pb-4' : '')">
                        <label class="form-label">Email</label>
                        <input type="email" class="form-control" required v-show="!isEsign" aria-label="Email">
                      </div>
                    </div>

                    <!-- signature pad -->
                    <div class="mb-3">
                      <label class="form-label">Draw Your Signature</label>
                      <div class="border" style="height:150px;position:relative;border-radius: 20px;">
                        <template v-if="previewSigUrl">
                          <img :src="previewSigUrl" class="w-100 h-100 object-fit-contain" />
                        </template>
                        <template v-else>
                          <canvas ref="avidCanvas" class="w-100 h-100" @click="hasStartedSigning = true;"></canvas>
                          <small v-if="!signaturePresent && !isEsign" class="position-absolute top-50 start-50 translate-middle text-muted">Sign Here</small>
                        </template>
                      </div>
                      <div class="mt-2 d-flex justify-content-between" v-if="!isEsign">
                        <button class="btn btn-secondary btn-sm" type="button" @click="clearSignature">Clear</button>
                        <input type="hidden" v-model="form.signature" />
                      </div>
                    </div>
                    <!-- Avid reps don't need to agree to terms -->
                    <button class="btn btn-primary w-100" :disabled="loading" v-show="!isEsign">Sign</button>
                  </form>
                </template>
                <template v-else>
                  <p class="text-muted">Awaiting Avid signature...</p>
                </template>
              </div>

              <!-- Client/PaidBy -->
              <div class="col-md-6 mb-4 ps-md-3">
                <h5>{{ headlineName}}</h5>
                <template v-if="paidSigned">
                    <img :src="getSigUrl(paidSigned)" alt="signature" class="img-fluid" /><br>
                    Signed by {{ paidSigned.signer_name }}<br>
                    <small>{{ datetime(paidSigned.signed_at) }}</small><br>
                </template>
                <template v-else-if="!isAvidUser || isEsign">
                  <form @submit.prevent="submitSignature">
                    <div class="row mb-3" :class="(isEsign ? 'border-0 border-bottom pb-4' : '')">
                      <div class="col">
                        <label class="form-label">First Name</label>
                        <input v-model="form.first_name" class="form-control" required v-show="!isEsign" aria-label="First Name">
                      </div>
                      <div class="col">
                        <label class="form-label">Last Name</label>
                        <input v-model="form.last_name" class="form-control" required v-show="!isEsign" aria-label="Last Name">
                      </div>
                    </div>
                    <div class="mb-3" :class="(isEsign ? 'border-0 border-bottom pb-4' : '')">
                      <label class="form-label">Email</label>
                      <input v-model="form.email" type="email" class="form-control" required v-show="!isEsign" aria-label="Email">
                    </div>
                    <div class="mb-3">
                      <label class="form-label">Draw Your Signature</label>
                      <div class="border" style="height:150px;position:relative; border-radius: 20px;">
                        <template v-if="previewSigUrl">
                          <img :src="previewSigUrl" class="w-100 h-100 object-fit-contain" />
                        </template>
                        <template v-else>
                          <canvas ref="clientCanvas" class="w-100 h-100" @click="hasStartedSigning = true;"></canvas>
                          <small v-if="!signaturePresent && !isEsign" class="position-absolute top-50 start-50 translate-middle text-muted">Sign Here</small>
                        </template>
                      </div>
                      <div class="mt-2 d-flex justify-content-between" v-if="!isEsign">
                        <button class="btn btn-secondary btn-sm" type="button" @click="clearSignature">Clear</button>
                        <input type="hidden" v-model="form.signature" />
                      </div>
                    </div>
                    <div class="form-check mb-3">
                      <input class="form-check-input" type="checkbox" id="agree_terms2" v-model="form.agree_terms" required aria-label="Agree Terms">
                      <label class="form-check-label small" for="agree_terms2">
                        By signing this agreement, you agree to Avid's <a href="/terms_and_conditions" target="_blank">terms of service</a> and that you are authorized to sign on behalf of {{ headlineName }}.
                      </label>
                    </div>
                    <button class="btn btn-primary w-100" :disabled="loading" v-show="!isEsign">Sign</button>
                  </form>
                </template>
                <template v-else>
                  <p class="text-muted">Awaiting customer signature...</p>
                </template>
              </div>
            </div>

            <!-- Success message -->
            <div v-if="successMessage" class="alert alert-success">{{ successMessage }}</div>            
          </div>

        </div>
      </div>

      <!-- Right image column -->
      <div class="col-lg-3 d-none d-lg-block bg-with-image"></div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SignaturePad from 'signature_pad';
import html2canvas from 'html2canvas';
import Swal from 'sweetalert2';
import { each } from 'jquery';
export default {
  props: {
    proposal: {type:Object,required:true},
    user: {type:Object,default:null}
  },
  data() {
    return {
      local_proposal: this.proposal,
      form: {
        first_name: this.user?.first_name || '',
        last_name: this.user?.last_name || '',
        email: this.user?.email || '',
        signature:'',
        agree_terms:false,
      },
      loading: false,
      successMessage: null,
      signaturePad: null,
      hasStartedSigning: false,
      validationErrors: {
        signature: '',
        agree_terms: '',
        first_name: '',
        last_name: '',
        email: ''
      },
      previewSigUrl: '',
    };
  },
  computed: {
    headlineName() {
      const pb = this.local_proposal.paid_by;
      return pb?.name || pb?.company_name || '';
    },
    showSubHeadline() {
      return this.local_proposal.client && this.local_proposal.client.id !== this.local_proposal.paid_by_id;
    },
    sortedLineItems() {
      return [...this.local_proposal.line_items].sort((a,b) => a.order - b.order);
    },
    netTotal() {
      return this.local_proposal.line_items.reduce((sum, li) => sum + Number(li.net_amount), 0);
    },
    // Total tax across all line items
    taxTotal() {
      return this.local_proposal.line_items.reduce((sum, li) => sum + Number(li.tax_amount), 0);
    },

    // Grand total amount due: net amount plus tax
    totalAmountDue() {
      return this.netTotal + this.taxTotal;
    },
    signed() {
      return this.local_proposal.status === 'signed';
    },
    effectiveDate() {
      return moment(this.local_proposal.start_date || new Date()).utc().format('MMMM D, YYYY');
    },
    agreementEndDate() {
      const startDate = moment(this.local_proposal.start_date || new Date()).utc();
      const termMonths = Number(this.local_proposal.term) || 0;
      // Subtract one day from the end date, because you are a detail-oriented legend
      return startDate.add(termMonths, 'month').subtract(1, 'day').format('MMMM D, YYYY');
    },
    agreementPeriod() {
      const startDate = moment(this.local_proposal.start_date || new Date()).utc().format('MMMM D, YYYY');
      const endDate = this.agreementEndDate;
      return `${startDate} and ${endDate}`;
    },
    signaturePresent() {
       return !!this.form.signature || this.hasStartedSigning;
    },
    avidSigned(){
      return this.local_proposal.signatures?.find(s=>s.signer_email.endsWith('@avidai.com'))
    },
    paidSigned(){return this.local_proposal.signatures?.find(s=>!s.signer_email.endsWith('@avidai.com'))},
    isAvidUser(){return this.user && this.user.email.endsWith('@avidai.com')},
    //If the querystring has ?esign=1, then we are in esign mode
    isEsign(){
      const urlParams = new URLSearchParams(window.location.search);
      const modalParam = urlParams.get('esign');
      return modalParam === '1'
    },
    // proposal expired flag
    expired(){return this.local_proposal.status === 'expired'},

    // classify items (fallback logic: recurring vs one-time)
    dueNowItems() {
      return this.sortedLineItems.filter(i => this.isDueNow(i));
    },
    dueLaterItems() {
      return this.sortedLineItems.filter(i => !this.isDueNow(i));
    },

    // totals for the footer
    oneTimeSubtotal() {
      return this.dueNowItems.reduce((s,i)=> s + Number(i.net_amount/i.term || 0), 0);
    },
    oneTimeDiscountTotal() {
      return this.dueNowItems.reduce((s,i)=> s + Math.max(0, Number(i.total_amount/i.term||0) - Number(i.net_amount/i.term||0)), 0);
    },
    oneTimeTaxTotal() {
      return this.dueNowItems.reduce((s,i)=> s + Number(i.tax_amount/i.term || 0), 0);
    },
    dueNowTotal() {
      // "Due now" = subtotal + taxes
      return this.oneTimeSubtotal + this.oneTimeTaxTotal;
    },
    showQuantityColumn() {
      // Show quantity column only if any item has quantity > 1
      return this.sortedLineItems.some(item => this.qty(item) > 1);
    },
  },
  mounted() {
    this.initializeSignaturePad();
    if(this.isAvidUser && !this.isEsign){
      this.form.first_name = this.user.first_name;
      this.form.last_name = this.user.last_name;
      this.form.email = this.user.email;
      this.form.agree_terms = true;
    }
  },
  methods: {
    currency(val) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
    },
    discountPercent(item) {
      if (item.total_amount === 0) return 0;
      return Math.round((1 - item.net_amount / item.total_amount) * 100);
    },
    clearSignature() {
        if (this.signaturePad) {
          this.signaturePad.clear();
        }
        this.form.signature = '';
        this.hasStartedSigning = false;
        this.previewSigUrl = '';
     },
    initializeSignaturePad() {
      // Wait for next tick to ensure DOM is fully rendered
      this.$nextTick(() => {
        // Try to find either canvas (avid or client)
        const canvas = this.$refs.avidCanvas || this.$refs.clientCanvas;
        
        if (!canvas) {
          console.warn('Canvas element not found, signature pad will not be initialized');
          return;
        }

        // resize canvas
        const resize = () => {
          if (!canvas) return;
          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          canvas.width = canvas.offsetWidth * ratio;
          canvas.height = canvas.offsetHeight * ratio;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.scale(ratio, ratio);
          }
        };
        
        window.addEventListener('resize', resize);
        resize();
        this.signaturePad = new SignaturePad(canvas);
      });
    },
    reinitializeSignaturePad() {
      // Method to reinitialize signature pad if needed
      if (this.signaturePad) {
        this.signaturePad.clear();
      }
      this.initializeSignaturePad();
    },
    async submitSignature() {
      // Reset validation errors
      this.validationErrors = { signature: '', agree_terms: '', first_name:'', last_name:'', email:'' };

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if(!this.form.first_name?.trim()) this.validationErrors.first_name = 'First name is required.';
      if(!this.form.last_name?.trim()) this.validationErrors.last_name = 'Last name is required.';
      if(!this.form.email?.trim()) {
        this.validationErrors.email = 'Email is required.';
      } else if(!emailRegex.test(this.form.email)) {
        this.validationErrors.email = 'Email is invalid.';
      }

      // Validate signature presence
      if (!this.signaturePad || this.signaturePad.isEmpty()) {
        this.validationErrors.signature = 'Signature is required.';
      }

      // Validate agree terms for non-Avid users
      if (!this.isAvidUser && !this.form.agree_terms) {
        this.validationErrors.agree_terms = 'You must agree to the terms.';
      }

      if (Object.values(this.validationErrors).some(v=>v)) {
        return; // stop submission if any validation failed
      }

      this.loading = true;

      // Capture signature image
      if (this.signaturePad) {
        this.form.signature = this.signaturePad.toDataURL('image/png');
      }

      try {
        const response = await axios.post(`/api/proposal/${this.local_proposal.id}/sign`, {
          ...this.form,
          agree_terms: this.form.agree_terms ? 1 : 0,
        });

        this.successMessage = 'Thank you! Your proposal has been signed.';

        // Optimistically add signature so UI updates without refresh
        this.local_proposal.signatures = [
          ...(this.local_proposal.signatures || []),
          {
            signer_name: `${this.form.first_name} ${this.form.last_name}`,
            signer_email: this.form.email,
            signature_image_path: this.form.signature, // using data URL until page refresh
            signed_at: new Date(),
          },
        ];

        // Check if we need to redirect to payment page
        if (response.data?.redirect_url) {
          Swal.fire({
            title: 'Success!',
            text: 'Your proposal has been signed; press continue to setup your payment method.',
            confirmButtonText: 'Continue',
            customClass: {
              popup: 'swal-success'
            },
          }).then(() => {
            window.location.href = response.data.redirect_url;
          });
        } else if (this.isAvidUser) {
          Swal.fire({
            title: 'Success!',
            text: 'Your signature has been successfully recorded.',
            confirmButtonText: 'Great!',
            customClass: {
              popup: 'swal-success'
            },
          });
          
          // Hide canvas and show the signature image
          this.previewSigUrl = this.form.signature;
        }
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed to sign proposal.';
        // console.log(msg);
        Swal.fire({
          title: 'Failed to save proposal',
          text: 'Something went wrong when saving your signature. Please refresh the page and try again.',
          customClass: {
            popup: 'swal-error'
          },
        });
      } finally {
        this.loading = false;
      }
    },
    getSigUrl(sig){
      if(!sig) return '';
      // if full URL already, return; else prepend https://storage.googleapis.com/
      return sig.signature_image_path;
    },
    isDueNow(item){
      return item.start_date === undefined || item.start_date === null; // force “later” unless you want immediate starts
    },

    qty(item){
      return Number(item.quantity || 1);
    },

    itemUnitPrice(item){
      // if backend gives per-unit, use it; otherwise derive from net_amount/qty
      if (item.unit_amount) return Number(item.unit_amount);
      const q = this.qty(item) || 1;
      return Number(item.net_amount) / q;
    },

    hasDiscount(item){
      return Number(item.total_amount) > Number(item.net_amount);
    },

    cadenceSuffix(item){
      // show “/ month” or “/ year”
      const interval = item.billing_frequency;
      if ( interval === 'monthly') return '/ month';
      if (interval === 'yearly') return '/ year';      ;
      if (interval === 'quarterly') return '/ quarter';
      return '';
    },

    paymentStart(item){
      const raw = item.start_date;
      // Parse as UTC and display in user's local timezone
      return moment(raw).utc().format('MMMM D, YYYY');
    },

    monthlyCost(item) {
      // If item has a start date, calculate monthly cost as total_amount / (term * quantity)
      if (item.start_date) {
        const totalAmount = Number(item.total_amount) || 0;
        const term = Number(item.term) || 1;
        const quantity = Number(item.quantity) || 1;
        return totalAmount / (term * quantity);
      }
      
      // Otherwise, calculate monthly cost based on billing frequency
      const netAmount = Number(item.net_amount) || 0;
      const frequency = item.billing_frequency?.toLowerCase() || 'monthly';
      
      switch (frequency) {
        case 'monthly':
          return netAmount;
        case 'quarterly':
          return netAmount / 3;
        case 'annually':
        case 'yearly':
          return netAmount / 12;
        case 'semi-annually':
          return netAmount / 6;
        default:
          return netAmount; // Default to monthly if unknown
      }
    },

    termDisplay(item) {
      // Show term in months
      const term = Number(item.term) || 0;
      if (!term) return '';
      
      if (term === 1) return '1 month';
      if (term < 12) return `${term} months`;
      
      const years = Math.floor(term / 12);
      const months = term % 12;
      
      if (months === 0) {
        return years === 1 ? '1 year' : `${years} years`;
      }
      
      return `${years}y ${months}m`;
    },

    dateRangeDisplay(item) {
      // Calculate start and end dates based on proposal start_date and item term
      const proposalStart = this.local_proposal.start_date || new Date();
      const startDate = moment(proposalStart).local();
      const termMonths = Number(item.term) || 0;
      const endDate = startDate.add(termMonths, 'month');
      
      return `${startDate.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}`;
    },

    timePeriodDisplay(item) {
      // Calculate start and end dates for the item's time period based on payment date
      // For items with start_date (due later), use that date (payment date)
      // For items without start_date (due now), use actual payment start date (signed date or today), not proposal start_date
      let paymentStartDate;
      if (item.start_date) {
        // Item has a specific payment start date - use it
        paymentStartDate = moment(item.start_date).utc();
      } else {
        // Item is "due now" - use signed date if available, otherwise today (actual payment date)
        // Don't use proposal.start_date as that's the agreement date, not the payment date
        const signedDate = this.paidSigned?.signed_at || this.avidSigned?.signed_at;
        paymentStartDate = signedDate ? moment(signedDate).utc() : moment().utc().startOf('day');
      }

      const term = Number(item.term) || 0;
      const billingFrequency = item.billing_frequency?.toLowerCase() || 'monthly';

      // Term is the number of billing frequency instances, not months
      // Convert term to actual months based on billing frequency (matching backend logic)
      let endDate;
      if (billingFrequency === 'once') {
        endDate = moment(paymentStartDate);
      } else if (billingFrequency === 'quarterly') {
        // Term is in quarters, so multiply by 3 to get months
        endDate = moment(paymentStartDate).add(term * 3, 'month').subtract(1, 'day');
      } else if (billingFrequency === 'yearly' || billingFrequency === 'annually') {
        // Term is in years, so use addYears
        endDate = moment(paymentStartDate).add(term, 'year').subtract(1, 'day');
      } else {
        // Monthly (or default fallback)
        endDate = moment(paymentStartDate).add(term, 'month').subtract(1, 'day');
      }
      
      return `${paymentStartDate.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}`;
    },
  }
};
</script>